<script setup lang="ts">
import { OnClickOutside } from "@vueuse/components";
import { formatCurrency } from "shared/helpers";

import { useCartStore } from "~/stores/cart";
import { useModalStore } from "~/stores/modal";

const { t } = useI18n();
const { isSafari } = useDevice();
const { cartModal } = useModalStore();
const cartStore = useCartStore();
const windowSize = useWindowSize();

let bodyEl: HTMLElement;

watch(() => cartModal.isModalOpened, () => {
	if (cartModal.isModalOpened) {
		bodyEl = document.body;
		bodyEl.style.overflow = "hidden";
	} else {
		bodyEl.style.overflow = "";
	}
});
</script>

<template>
	<teleport to="body">
		<div v-if="cartModal.isModalOpened" class="fixed top-0 left-0 z-10 w-screen h-screen flex justify-end bg-black/40 duration-125"></div>
		<OnClickOutside @trigger="cartModal.closeModal">
			<Transition :name="windowSize.width.value > 1024 ? 'slide' : ''">
				<div
					v-if="cartModal.isModalOpened"
					class="flex flex-col fixed top-0 right-0 bg-white w-[420px] h-screen z-40 sm:w-screen xs:w-screen"
					:class="isSafari && 'pb-10'"
				>
					<div class="flex items-center justify-between px-6 py-5 border-b border-gray font-medium text-lg uppercase xs:px-4">
						<span class="ml-1">{{ t('cart') }} ({{ cartStore.getCartItemsAdapted.length }})</span>
						<AppButtonIcon
							name="ic:round-close"
							@click="cartModal.closeModal"
						/>
					</div>
					<template v-if="cartStore.cart.length">
						<CartList class="h-full overflow-y-scroll my-4 pl-6 pr-4 mr-4 xs:px-4" />
						<div class="flex flex-col px-6 py-5 border-t border-gray xs:px-4">
							<div class="flex justify-between font-medium text-sm">
								<span class="uppercase">{{ t('total') }}:</span>
								<div class="flex gap-x-1">
									<span
										v-if="cartStore.getCartSummary.subtotal !== cartStore.getCartSummary.total"
										class="line-through font-normal text-green-dark"
									>
										{{ formatCurrency(cartStore.getCartSummary.subtotal) }}
									</span>
									<span>{{ formatCurrency(cartStore.getCartSummary.total) }}</span>
								</div>
							</div>
							<AppButton
								class="mt-5"
								@click="() => {
									cartModal.closeModal();
									navigateTo('/order/cart');
								}"
							>
								{{ t('viewCart') }}
							</AppButton>
							<AppButton
								class="mt-2"
								fill
								@click="() => {
									cartModal.closeModal();
									navigateTo('/order/checkout');
								}"
							>
								{{ t('checkoutOrder') }}
							</AppButton>
						</div>
					</template>
					<div class="flex justify-center items-center grow">
						<CartEmpty
							v-show="!cartStore.cart.length"
							class="mb-10"
							@click:add="cartModal.closeModal()"
						/>
					</div>
				</div>
			</Transition>
		</OnClickOutside>
	</teleport>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 300ms ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
	right: -420px;
}
</style>
